import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  Injector,
  input,
  output,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';

import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputMaskModule } from 'primeng/inputmask';
import { ChipModule } from 'primeng/chip';
import { ValidationErrorsDictionary } from '../../../../core/dictionary/validation-errors.dictionary';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NoWhitespaceDirective } from '../../../../core/directives/no-whitespaces.directive';
import { UppercaseDirective } from '../../../../core/directives/uppercase.directive';

export type LabelMode = 'float' | 'ifta' | 'static' | 'none';
export type InputSize = 'small' | 'large';
export type IconPosition = 'left' | 'right';

export interface TextInputIcon {
  icon: string;
  position: IconPosition;
  clickable?: boolean;
}

@Component({
  selector: 'app-text-chip-input',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    FloatLabelModule,
    IftaLabelModule,
    IconFieldModule,
    InputIconModule,
    InputMaskModule,
    ChipModule,
    NoWhitespaceDirective,
    UppercaseDirective,
  ],
  templateUrl: './text-chip-input.component.html',
  styleUrl: './text-chip-input.component.css',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppTextChipInputComponent),
      multi: true,
    },
  ],
})
export class AppTextChipInputComponent implements ControlValueAccessor, AfterViewInit {

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private ngControl: NgControl | null = null;

  readonly inputId = input<string>(`chip-input-${crypto.randomUUID().slice(0, 8)}`);
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly labelMode = input<LabelMode>('float');
  readonly size = input<InputSize | undefined>(undefined);
  readonly fluid = input<boolean>(true);
  readonly helpText = input<string>('');
  readonly filled = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly type = input<string>('text');
  readonly mask = input<string | null>(null);
  readonly slotChar = input<string>('_');
  readonly icon = input<TextInputIcon | null>(null);
  readonly showErrors = input<boolean>(true);
  readonly maxlength = input<number | null>(null);
  readonly name = input<string>('');
  readonly readonly = input<boolean>(false);
  readonly autofocus = input<boolean>(false);
  readonly noWhitespace = input<boolean>(false);
  readonly uppercase = input<boolean>(false);
  readonly unique = input<boolean>(true);

  readonly focused = output<FocusEvent | Event>();
  readonly blurred = output<FocusEvent | Event>();
  readonly iconClicked = output<void>();
  readonly chipsChanged = output<string[]>();

  readonly inputValue = signal<string>('');
  readonly isFocused = signal<boolean>(false);
  readonly isTouched = signal<boolean>(false);
  readonly isDisabled = signal<boolean>(false);

  readonly chips = signal<string[]>([]);

  readonly controlState = signal<{
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
    errors: any;
  }>({
    invalid: false,
    touched: false,
    dirty: false,
    errors: null,
  });

  readonly computedDisabled = computed(() => this.disabled() || this.isDisabled());

  readonly hasRequiredRule: Signal<boolean> = computed(() => {
    const state = this.controlState();
    return state.errors?.['required'] !== undefined;
  });

  readonly isInvalid = computed(() => {
    const state = this.controlState();
    return state.invalid && (state.touched || state.dirty);
  });

  readonly errorMessage = computed(() => {
    const state = this.controlState();
    if (!state.errors) return null;
    return ValidationErrorsDictionary.resolveValidationErrors(state.errors);
  });

  readonly useMask = computed(() => !!this.mask());
  readonly hasLabel = computed(() => !!this.label() && this.labelMode() !== 'none');
  readonly hasIcon = computed(() => !!this.icon());
  readonly hasChips = computed(() => this.chips().length > 0);

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string[] | null): void {
    this.chips.set(Array.isArray(value) ? [...value] : []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl, null, {
      optional: true,
      self: true,
    });

    if (this.ngControl?.control) {
      this.ngControl.control.statusChanges
        ?.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.updateControlState());

      this.updateControlState();
    }
  }

  protected commitInputValue(): void {
    const raw = this.inputValue().trim();
    if (!raw) return;

    if (this.unique() && this.chips().includes(raw)) {
      this.inputValue.set('');
      return;
    }

    const next = [...this.chips(), raw];
    this.chips.set(next);
    this.inputValue.set('');
    this.emitChange(next);
  }

  protected removeChip(index: number): void {
    const next = this.chips().filter((_, i) => i !== index);
    this.chips.set(next);
    this.emitChange(next);
    this.updateControlState();
  }

  protected clearAll(): void {
    this.chips.set([]);
    this.emitChange([]);
    this.updateControlState();
  }

  private emitChange(value: string[]): void {
    this.onChange(value);
    this.chipsChanged.emit(value);
  }

  protected onFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  protected onMaskFocus(event: Event): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  protected onBlur(event: FocusEvent): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this.commitInputValue();
    this.onTouched();
    this.blurred.emit(event);
    this.updateControlState();
  }

  protected onMaskBlur(event: Event): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this.commitInputValue();
    this.onTouched();
    this.blurred.emit(event);
    this.updateControlState();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitInputValue();
    }
  }

  protected onInput(event: Event): void {
    this.inputValue.set((event.target as HTMLInputElement).value);
  }

  protected onMaskInput(event: Event): void {
    this.inputValue.set((event.target as HTMLInputElement).value ?? '');
  }

  protected onIconClick(): void {
    if (this.icon()?.clickable) {
      this.iconClicked.emit();
    }
  }

  private updateControlState(): void {
    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;
      this.controlState.set({
        invalid: ctrl.invalid,
        touched: ctrl.touched,
        dirty: ctrl.dirty,
        errors: ctrl.errors,
      });
    }
  }
}
